import type { WhiteboardTranslations } from "./translations";

export const EN_TRANSLATIONS: WhiteboardTranslations = {
  workspace: {
    cancelLabel: "Cancel",
    syncingSession: "Syncing lesson session...",
    initializingBoard: "Initializing whiteboard...",
    clearSlideModalTitle: "Clear current slide?",
    clearSlideModalDescription:
      "This action will clear all drawings and widgets on the current slide.",
    clearSlideConfirmLabel: "Clear slide",
    slideClearedToastTitle: "Slide cleared",
    slideClearedToastDesc: "All drawings on the current slide have been cleared.",
    slowNetworkAria: "Slow network connection notice",
    slowNetworkTitle: "Slow network connection",
    slowNetworkDesc: "— Syncing online session. You are currently working in local draft mode.",
    retrySyncButton: "Retry sync",
    toolErrorTitle: "Whiteboard encountered an issue",
    toolErrorFallback: "An unexpected error occurred while running this tool.",
    toolReloadButton: "Reload Whiteboard",
    dismissToastAria: "Dismiss notification",
  },
  hud: {
    appMenuAriaLabel: "Application menu",
    boardMenuAriaLabel: "Whiteboard menu",
    exportPng: "Export image (PNG)",
    exportPdf: "Export handout (PDF)",
    pdfScopeAriaLabel: "PDF export scope",
    pdfScopeAll: "Entire lesson",
    pdfScopeTab: "Current tab",
    pdfScopeSlide: "Current slide",
    findOnCanvas: "Find on canvas",
    help: "Help",
    clearSlide: "Clear current slide",
    paperStyleSectionTitle: "Paper Style",
    strokeSmoothingSectionTitle: "Stroke Smoothing",
    canvasBgSectionTitle: "Canvas Background",
    canvasBgSwatchAria: (color: string) => `Canvas background ${color}`,
    customBgColorAria: "Custom background color",
    paperPresets: {
      blank: "Blank",
      "grid-small": "Small Grid",
      "grid-large": "Large Grid",
      dots: "Dotted",
      lined: "Ruled Lines",
      tianzige: "Tianzige (田字格)",
      mizige: "Mizige (米字格)",
      "pinyin-tianzige": "Pinyin + Tianzige",
    },
    prevSlideAria: "Previous slide",
    nextSlideAria: "Next slide",
    addSlideAria: "Add new slide",
    duplicateSlideAria: "Duplicate slide",
    duplicateSlideTitle: "Duplicate current slide",
    deleteSlideAria: "Delete current slide",
    confirmDeleteSlideAria: "Confirm slide deletion",
    confirmDeleteSlideLabel: "Confirm delete?",
    cancelDeleteSlideAria: "Cancel slide deletion",
    cancelLabel: "Cancel",
    drawingToolbarAria: "Drawing and learning materials toolbar",
    pointerSelectionLabel: "Move",
    pointerHandLabel: "Hand tool",
    shapesMenuAria: "Select shape",
    shapeTools: {
      rectangle: "Rectangle",
      diamond: "Diamond",
      ellipse: "Ellipse",
      arrow: "Arrow",
      line: "Line",
    },
    freedrawAria: "Pen",
    freedrawTitle: "Freedraw pen (P)",
    textAria: "Text",
    textTitle: "Text (T)",
    eraserAria: "Eraser",
    eraserTitle: "Eraser (E)",
    insertMenuTriggerAria: "Insert image, document, and video",
    insertMenuTriggerTitle: "Insert image, PDF, or video",
    insertPopoverAria: "Insert learning materials and media",
    insertImageLabel: "Insert image",
    pinyinAssistantTrigger: "Pinyin & Hanzi Assistant",
    stemTriggerAria: "STEM, Math, and Shape Library tools",
    stemTriggerTitle: "LaTeX Formula, Simulation & STEM Library",
    stemPopoverAria: "Math, Simulation, and STEM Library tools",
    stemLibraryTrigger: "STEM & Shape Library",
    stemAllSubjects: "All",
    stemGenerateSectionTitle: "Generate",
    stemSidebarTitle: "Library",
    stemSidebarCloseAria: "Close library",
    stemCategoryLabels: {
      physics: { label: "Physics", description: "Circuits, Optics, Mechanics" },
      math: { label: "Mathematics", description: "2D/3D Geometry, Axes, Graphs" },
      chemistry: { label: "Chemistry", description: "Labware, Molecular Structures" },
      cs: { label: "CS & Flowcharts", description: "Flowcharts, Networks, Architecture" },
      chinese: {
        label: "Chinese Pedagogy (Grids / Strokes)",
        description: "Tianzige, Mizige, 8 Basic Strokes, Character Structure & Tones",
      },
    },
    latexTrigger: "Insert Math / LaTeX Formula",
    simulationTrigger: "Graphing & Geometry",
    youtubeTrigger: "Embed YouTube Video",
    laserAria: "Laser pointer",
    laserTitle: "Laser pointer (K)",
    cursorChatTriggerAria: "Live cursor chat",
    cursorChatPlaceholder: "Say something...",
    cursorChatHint: "Enter to pin · Esc to close",
    remoteHostBadge: "Host",
    remoteOverflowBadge: (count: number) => `+${count} more`,
    teacherPermissionsAria: "Student interaction settings",
    screenFollowYou: "Follow your screen",
    screenFreeMove: "Free movement",
    screenFollowingTeacher: "Following teacher",
    drawCustom: "Select students",
    drawViewOnly: "View only",
    undoAria: "Undo",
    undoTitle: "Undo (Ctrl+Z)",
    redoAria: "Redo",
    redoTitle: "Redo (Ctrl+Y)",
    zoomOutAria: "Zoom out",
    zoomInAria: "Zoom in",
    zoomMenuAria: "Viewport zoom options",
    zoomMenuHeader: (percent: number) => `Viewport (${percent}%)`,
    fitSlide: "Fit entire board",
    fitWidth: "Fit board width",
    zoom100Label: "Zoom 100% (1:1)",
    pageNumberInputAria: "Jump to page number",
  },
  permissions: {
    teacherDialogTitle: "Student Interaction Settings",
    teacherScreenSection: "Student Screen Mode",
    followYouBtn: "Follow your screen",
    freeMoveBtn: "Free movement",
    teacherFollowHint:
      "Students always see the exact board area you are viewing and cannot pan away.",
    teacherFreeHint: "Students can freely zoom and pan around the board.",
    drawingPermissionSection: "Student Drawing Permissions",
    viewOnlyMode: "View only",
    customMode: "Select students",
    viewOnlyHint: "Only you can draw; students view the board.",
    customModeHint: "Select students who can draw.",
    searchParticipantPlaceholder: "Search participants",
    selectedCount: (editable: number, total: number) => `Selected: ${editable}/${total}`,
    deselectAllBtn: "Deselect all",
    selectStudentAria: (name: string) => `Select ${name}`,
  },
  pinyinModal: {
    title: "Pinyin & Hanzi Flashcard Assistant",
    subtitle:
      "Type numbered Pinyin (e.g. ni3 hao3, lv4) to convert tone marks and generate grouped Tianzige/Mizige character cards.",
    modeCardLabel: "Pinyin + Hanzi Grid Card (田字格/米字格)",
    modeCardDesc: "Insert grouped vector Pinyin stave + Hanzi practice cells at the center of the canvas",
    modeTextLabel: "Plain Pinyin / Hanzi Text Only",
    modeTextDesc: "Insert tone-marked Pinyin and Hanzi text elements directly onto the canvas",
    pinyinInputLabel: "Pinyin Input (supports tone numbers 1-5, v or u: for ü)",
    pinyinInputPlaceholder: "e.g. ni3 hao3, zhong1 wen2, lv4 se4...",
    convertedPreviewLabel: "Tone-Marked Pinyin Preview",
    hanziInputLabel: "Hanzi Characters (1 per cell)",
    hanziInputPlaceholder: "e.g. 你好, 中文...",
    gridStyleLabel: "Practice Grid Style",
    gridTianzige: "Tianzige (田字格)",
    gridMizige: "Mizige (米字格)",
    toneVowelsTitle: "Tone Marked Vowels (Click to insert)",
    punctuationTitle: "Chinese Punctuation",
    colorLabel: "Stroke Color",
    strokeColorLabels: {
      ink: "Ink",
      white: "White",
      crimson: "Crimson",
      blue: "Blue",
      emerald: "Emerald",
      amber: "Amber",
    },
    copyPinyinButton: "Copy Pinyin",
    copiedPinyinButton: "Copied!",
    cancelButton: "Cancel",
    insertCardButton: "Insert Character Card",
    insertTextButton: "Insert Text",
    practiceTitle: "Teaching Worksheet Mode",
    practiceFull: "Standard (Pinyin + Hanzi)",
    practiceTrace: "Tracing Sheet (Model + Trace)",
    practiceDictation: "Dictation (Pinyin Only)",
    practicePinyinQuiz: "Tone Quiz (Hanzi Only)",
    gridJiugongge: "Jiugongge (九宫格)",
    gridSikexian: "4-Line Pinyin (四线格)",
    presetsTitle: "Quick Classroom Vocabulary & Strokes",
    cellsCountBadge: (count: number) => `${count} cell${count === 1 ? "" : "s"}`,
  },
  modals: {
    cancel: "Cancel",
    close: "Close",
    confirm: "Confirm",
    latexEditTitle: "Edit Math & Science Formula (LaTeX)",
    latexCloseTitle: "Close dialog (Esc)",
    latexChipInsertTitle: (latex: string) => `Insert ${latex}`,
    latexChips: {
      systemCases: "System of equations",
      orBrackets: "Disjunction cases",
      derivativePrime: "Derivative f'(x)",
      derivative: "Differential d/dx",
      partialDerivative: "Partial derivative",
      integral: "Integral",
      limit: "Limit",
      sum: "Sigma sum",
      matrix2x2: "2x2 Matrix",
      iff: "⇔ Equivalent",
      implies: "⇒ Implies",
      inSet: "∈ Element of",
      realSet: "ℝ Real numbers",
      vector: "Vector AB",
      combination: "Combination C_n^k",
      subscript: "Subscript",
      delta: "Δ Delta",
      alpha: "α Alpha",
      beta: "β Beta",
      lambda: "λ Lambda",
      plusMinus: "± Plus-minus",
      approx: "≈ Approx",
      infinity: "∞ Infinity",
    },
    latexInkColors: {
      defaultDark: "Default (Adaptive White)",
      defaultLight: "Default (Black)",
      amber: "Amber",
      blue: "Blue",
      emerald: "Emerald",
      rose: "Rose",
      purple: "Purple",
    },
    latexInputPlaceholder: "e.g. \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
    latexSyntaxWarning: "Syntax warning",
    latexEmptyPreviewHint: "Enter a LaTeX formula above to preview",
    latexInkColorLabel: "Ink color:",
    latexInsertingButton: "Inserting...",
    latexUpdateButton: "Update formula",
    latexInsertButton: "Insert formula",
    simulationTitle: "Graphing & Geometry Boards",
    simulationSubtitle: "Choose a coordinate graphing board or plane geometry board to insert into the lesson",
    simulationInsertButton: "Insert to board",
    studios: {
      graphing: {
        title: "Function Graphing & Calculus",
        subtitle: "Cartesian Oxy Coordinate Plane",
        description:
          "Plot functions, inequality regions, circles, and ellipses on the Oxy plane. Supports parameter sliders, tangent lines, integral shading, and finding roots and extrema.",
        tags: ["Function Plots", "Inequalities", "Parameters", "Tangent Lines", "Extrema & Roots"],
      },
      geometry: {
        title: "Plane Geometry",
        subtitle: "Interactive Geometric Constructions",
        description:
          "Construct points, segments, perpendicular and parallel lines, circles, polygons, and standard textbook figures with interactive vertex dragging.",
        tags: ["Points & Segments", "Perpendicular & Parallel", "Circles", "Polygons", "Textbook Figures"],
      },
    },
    youtubeTitle: "Embed YouTube Video",
    youtubeSubtitle: "Embed a YouTube video with classroom-synced playback controls",
    youtubeUrlLabel: "YouTube URL",
    youtubeUrlPlaceholder: "https://www.youtube.com/watch?v=... or https://youtu.be/...",
    youtubeInvalidUrlError: "Invalid URL. Supports watch, youtu.be, shorts, and embed formats.",
    youtubeDisplayTitleLabel: "Display Title",
    youtubePreviewLabel: "Preview (16:9)",
    youtubeStartTimeBadge: (seconds: number) => `Start: ${seconds}s`,
    youtubeInsertButton: "Insert to board",
    videoUploadTitle: "Upload Lesson Video",
    videoUnsupportedError: "Only MP4 and WebM videos are supported (max 250MB)",
    videoPresignFailedError: (status: number) => `Failed to initialize upload (${status})`,
    videoUploadHttpFailedError: (status: number) => `Upload to MinIO failed (HTTP ${status})`,
    videoUploadNetworkError: "Network error while uploading video to MinIO",
    mermaidTitle: "Mermaid to Diagram",
    mermaidSubtitle: "Generate flowcharts, sequence diagrams, or structures onto the canvas",
    mermaidCloseAria: "Close",
    mermaidTemplatesLabel: "Templates:",
    mermaidTemplateFlowchart: "Flowchart",
    mermaidTemplateSequence: "Sequence",
    mermaidTemplateClass: "Class Diagram",
    mermaidSyntaxLabel: "Mermaid Syntax:",
    mermaidKeyboardHint: "Ctrl + Enter to insert",
    mermaidEmptyError: "Please enter Mermaid syntax.",
    mermaidNoElementsError: "No diagram elements could be produced from this definition.",
    mermaidSyntaxError: (msg: string) => `Mermaid syntax error: ${msg}`,
    mermaidSupportedDiagramsHint: "Supports Flowchart, Sequence, Class, and State diagrams",
    mermaidCancelButton: "Cancel",
    mermaidProcessingButton: "Processing...",
    mermaidInsertButton: "Insert to Canvas",
    webEmbedTitle: "Embed Web Resource",
    webEmbedSubtitle: "Embed interactive learning activities or web content",
    webEmbedCloseAria: "Close",
    webEmbedUrlLabel: "Resource URL (https://...)",
    webEmbedUrlPlaceholder: "https://example.com/interactive-app",
    webEmbedNameLabel: "Display Name (optional)",
    webEmbedNamePlaceholder: "Web Resource",
    webEmbedInvalidUrlError: "Invalid URL. Please enter a valid URL starting with https://",
    webEmbedCancelButton: "Cancel",
    webEmbedInsertButton: "Insert to Canvas",
    mathDockPlotToGraphTitle: "Plot expression on graphing board",
    mathDockPlotToGraphButton: "Plot",
    mathDockStampToBoardTitle: "Stamp formula onto whiteboard",
    mathDockStampToBoardButton: "To Board",
    mathDockCollapseTitle: "Collapse to minimal bar",
    mathDockExpandTitle: "Expand full math tools & textbook templates",
    mathDockCollapseButton: "Compact",
    mathDockExpandButton: "Expand",
    mathDockDoneButton: "Done",
    mathDockTabAlgebra: "123 Algebra & Numbers",
    mathDockTabFunctions: "f(x) Trig & Functions",
    mathDockTabPresets: "★ Textbook Presets",
    mathDockTabCalculus: "∫∑ Calculus & LaTeX",
    mathDockTemplates: {
      cubic_m: "Cubic Function (with Parameter m)",
      biquadratic_m: "Biquadratic Function",
      rational: "Rational Function (Asymptotes)",
      parabola_m: "Quadratic Parabola",
      trig_wave: "Trigonometric Wave",
      radical: "Radical Function",
      linear_ineq: "2D Linear Inequality Region",
      circle: "Circle Equation",
      ellipse: "Standard Ellipse",
    },
  },
  widgets: {
    modeToggleToAnnotateTitle: "Switch to annotation drawing mode",
    modeToggleToInteractTitle: "Switch to interactive mode",
    interactLabel: "Explore",
    annotateLabel: "Draw",
    unlockWidgetTitle: "Board locked (Click to unlock)",
    lockWidgetTitle: "Lock board position",
    lockedByTeacherTitle: "Board is locked by the teacher",
    maximizeWidgetTitle: "Maximize to fit screen",
    restoreWidgetTitle: "Restore previous view",
    confirmDeleteWidgetTitle: "Confirm deleting this board",
    confirmDeleteWidgetLabel: "Confirm delete?",
    cancelDeleteWidgetTitle: "Cancel delete",
    cancelDeleteWidgetLabel: "Cancel",
    deleteWidgetAria: "Delete board",
    resizeHandleTitle: "Drag to resize",
    microPromptTouchPoint: "1. Tap a reference segment or a point to pass through",
    microPromptPassThroughPoint: "2. Tap a point or any empty spot to pass through",
    microPromptPerpendicularEdge: "2. Tap a segment for perpendicular direction",
    microPromptParallelEdge: "2. Tap a segment for parallel direction",
    microPromptCancelTitle: "Cancel action (Esc)",
    openSlidersTitle: "Open parameter sliders panel",
    parametersLabel: "Parameters",
    collapseSlidersTitle: "Collapse parameter panel",
    collapseLabel: "Collapse",
    videoIndependentTitle: "In Independent watching mode - Click to sync with teacher",
    videoFollowingTitle: "Synced with teacher - Click to watch independently",
    videoIndependentLabel: "Independent",
    videoFollowTeacherLabel: "Follow Teacher",
    videoResyncButton: "Resume following teacher",
    videoResyncTitle: "Resume synchronized playback with teacher",
    hidePlotTitle: "Hide plot",
    showPlotTitle: "Show plot",
    hideInequalityTitle: "Hide feasible region",
    showInequalityTitle: "Show feasible region",
    hideConicTitle: "Hide conic",
    showConicTitle: "Show conic",
    inequalityShortTag: "ineq:",
    deleteExpressionTitle: (label: string) => `Delete ${label}`,
    invalidSyntaxLabel: "Invalid syntax",
    mobileExpressionListTitle: "Expressions & Calculus",
    addButtonLabel: "Add",
    openMobileFormulaSheetTitle: "Tap to open formula bar & touch keypad",
    emptyExpressionLabel: "(empty)",
    openAlgebraDrawerTitle: "Open algebra drawer (Expression list)",
    expressionListTitle: "Expressions",
    collapseAlgebraDrawerTitle: "Collapse algebra drawer [ ◀ ]",
    omniInputPlaceholder: "Enter function, inequality, circle/ellipse...",
    omniInputAria: "Add new expression",
    clickToAddExpression: "Click to enter expression...",
    newBadge: "+ New",
    addPointLabel: "+ Point",
    triangleAbcLabel: "Triangle ABC",
    triangleLabel: "Triangle",
    segmentLabel: "Segment",
    lineLabel: "Line",
    vectorLabel: "Vector",
    perpendicularLabel: "Perpendicular",
    parallelLabel: "Parallel",
    circleLabel: "Circle",
    polygonLabel: "Polygon",
    clearGeometryBoardTitle: "Clear geometry board",
    geometryPresets: {
      tri_altitudes: "Triangle & 3 Altitudes",
      tri_medians: "Triangle & 3 Medians",
      tri_circum: "Triangle & Circumcircle",
      tri_in: "Triangle & Incircle",
      tri_right: "Right Triangle",
      tri_iso: "Isosceles Triangle",
      tri_eq: "Equilateral Triangle",
      quad_para: "Parallelogram",
      quad_rect: "Rectangle",
      quad_rhomb: "Rhombus",
      quad_sq: "Square",
      circle_tan: "Circle & 2 Tangents",
      quad_inscribed: "Inscribed Quadrilateral",
      poly_pentagon: "Regular Pentagon",
      quad_trapezoid: "Isosceles Trapezoid",
    },
    extremumMaxBadge: (x: string, y: string) => `Max(${x}, ${y})`,
    extremumMinBadge: (x: string, y: string) => `Min(${x}, ${y})`,
    rootSweeperBadge: (paramName: string, val: string, count: number) =>
      `${paramName} = ${val} → ${count} ${count === 1 ? "root" : "roots"}`,
    lpUnboundedReasons: {
      both_unbounded: "Feasible region is unbounded - No Maximum/Minimum exists",
      max_unbounded: "No Maximum exists (tends to +∞ due to unbounded feasible region)",
      min_unbounded: "No Minimum exists (tends to -∞ due to unbounded feasible region)",
    },
    lpTableHeader: (fnExpr: string) => `Optimize F(x, y) = ${fnExpr}`,
    lpVertexCol: "Vertex",
    lpCoordsCol: "Coordinates",
    lpMaxSummary: (val: string, label: string, x: string, y: string) =>
      `★ max F = ${val} at ${label} (${x}, ${y})`,
    lpMinSummary: (val: string, label: string, x: string, y: string) =>
      `★ min F = ${val} at ${label} (${x}, ${y})`,
    stopwatchPauseTitle: "Pause simulation",
    stopwatchPlayTitle: "Play simulation",
    stopwatchResetTitle: "Reset time",
    riemannMethodLeft: "Left",
    riemannMethodMidpoint: "Midpoint",
    riemannMethodRight: "Right",
    riemannMethodTrapezoidal: "Trapezoid",
    riemannExpandTitle: "Click to expand Riemann Sum control bar",
    riemannCollapseTitle: "Collapse Riemann control bar",
    riemannCloseTitle: "Close Riemann Sum",
    riemannFullTitle: "Riemann Sum",
    riemannDec5Title: "Decrease by 5 partitions",
    riemannDec1Title: "Decrease by 1 partition",
    riemannInputTitle: "Enter partition count n directly (2 - 100)",
    riemannInc1Title: "Increase by 1 partition",
    riemannInc5Title: "Increase by 5 partitions",
    sweeperExpandTitle: "Click to expand y = m sweeper control bar",
    sweeperCollapseTitle: "Collapse y = m sweeper bar",
    sweeperCloseTitle: "Close y = m sweeper",
    sweeperFullTitle: "Intersection y = m",
    sweeperInputTitle: "Enter value of m directly",
    sweeperDec05Title: "Decrease by 0.5",
    sweeperDec01Title: "Decrease by 0.1",
    sweeperResetZeroTitle: "Reset to 0",
    sweeperInc01Title: "Increase by 0.1",
    sweeperInc05Title: "Increase by 0.5",
    insituCircumcircleLabel: "Circumcircle",
    insituIncircleLabel: "Incircle",
    insituAltitudesLabel: "Altitudes",
    insituMediansLabel: "Medians",
    insituBisectorsLabel: "Bisectors",
    insituMeasurementsLabel: "Measures",
    insituMidpointLabel: "Midpoint M",
    insituBisectorLabel: "Bisector d",
    insituLengthLabel: "Length",
    insituCircleHeader: "Circle",
    insituTwoTangentsLabel: (ptId: string) => `2 Tangents (${ptId})`,
    insituCreateTangentsLabel: "2 Tangents",
    insituPointLabelPrefix: "Point:",
    insituVertexAltitudeTitle: "Altitude from this vertex",
    insituVertexMedianTitle: "Median from this vertex",
    insituVertexBisectorTitle: "Angle bisector from this vertex",
    insituPolygonHeader: (count: number) => `Polygon (${count} vertices)`,
    insituDeleteElementTitle: "Delete element",
    insituCloseTitle: "Close",
    dockCompactTriggerTitle: "Open pedagogical features list",
    dockCompactTriggerLabel: "Features",
    dockMenuHeader: "Pedagogical Features",
    dockMoreActionsTitle: "View all other actions",
    dockMoreActionsLabel: (count: number) => `More (${count})`,
    dockAllActionsHeader: "All Actions",
    tangentOutOfDomainBadge: (x0: string) => `x₀ = ${x0} (out of domain)`,
    unmuteVideoLabel: "Unmute audio",
    snapIsoscelesBadge: "Iso",
    emptyFeasibleRegionNotice: "Empty feasible region",
    feasiblePointMemberBadge: (objValueStr: string) => `✓ In feasible region${objValueStr}`,
    feasiblePointViolatedBadge: (expr: string) => `✕ Violates: ${expr}`,
    pedagogicalActions: {
      hatchingLabel: "Cross-hatch Unfeasible",
      hatchingTitle: "Cross-hatch unfeasible half-planes (textbook style)",
      objectiveOptimizerLabel: "LP Objective Optimizer",
      objectiveOptimizerTitle: "Find maximum and minimum of objective function F(x, y)",
      curveIntersectionsLabel: (plotName: string, count: number) =>
        count > 0 ? `Intersections of ${plotName} (${count})` : `Intersections of ${plotName}`,
      curveIntersectionsTitle: (plotName: string) =>
        `Find intersections and axis projections of ${plotName} with other plots`,
      areaBetweenCurvesLabel: "Area Between 2 Curves",
      areaBetweenCurvesTitle: "Area of region bounded by two curves f(x) and g(x)",
      conicAxesLabel: (isEllipse: boolean) =>
        isEllipse ? "Show Center & Axes" : "Show Center & Radius",
      conicAxesTitle: "Display conic center, vertices, and axes",
      conicFociLabel: "Show Foci",
      conicFociTitle: "Display ellipse foci F₁, F₂ and focal distance",
      asymptotesLabel: "Show Asymptotes",
      asymptotesTitle: "Display vertical and horizontal asymptotes",
      symmetryCenterLabel: "Center of Symmetry",
      symmetryCenterTitle: "Coordinates of the curve's center of symmetry",
      quadraticVertexAxisLabel: "Show Vertex & Axis",
      quadraticVertexAxisTitle: "Parabola vertex coordinates and axis of symmetry",
      monotonicityLabel: "Monotonicity & Extrema",
      monotonicityTitle: "Monotonicity intervals and local extrema points",
      inflectionPointLabel: "Inflection Point",
      inflectionPointTitle: "Inflection point coordinates of cubic function",
      keyFeaturesLabel: "Key Features",
      keyFeaturesTitle: "Key feature points on the graph",
      curveProbeLabel: "Project Point M",
      curveProbeTitle: "Movable point M on curve with Ox, Oy coordinate projections",
      intervalAnalysisLabel: "Analyze [a, b]",
      intervalAnalysisTitle: "Analyze global maximum and minimum on interval [a, b] with draggable handles",
      rootSweeperLabel: "Intersection y = m",
      rootSweeperTitle: "Analyze number of roots of f(x) = m via horizontal line sweeper",
      tangentLineLabel: "Tangent at x₀",
      tangentLineTitle: "Construct tangent line to the curve at x₀",
      riemannSumLabel: "Riemann Sum",
      riemannSumTitle: "Approximate area under curve using Riemann integral sum",
      definiteIntegralLabel: "Definite Integral",
      definiteIntegralTitle: "Compute definite integral and planar area S",
      absFTransformLabel: "Reflect |f(x)|",
      absFTransformTitle: "Keep upper half above Ox and reflect lower half across Ox",
      fAbsTransformLabel: "Reflect f(|x|)",
      fAbsTransformTitle: "Keep right half of Oy and reflect symmetrically to the left",
    },
  },
};
